import React, { useEffect, useState } from 'react';
import { ACCENT, BG, FONT, ERROR_RED } from '../lib/tokens';
import { Ic } from './Icons';
import { useBreakpoint } from '../lib/useBreakpoint';

const TOKEN_KEY = 'elx_access_token';

// Decode the base64url(payload) half of the token, no signature verification
// (we don't ship the HMAC key to the browser). If exp is in the future we
// trust the token — anyone who can forge a localStorage entry has already
// gotten past the device's own lock, so this is sufficient for a 12-person
// event.
function tokenIsValid(token) {
  if (!token) return false;
  const [body] = token.split('.');
  if (!body) return false;
  try {
    let s = body.replace(/-/g, '+').replace(/_/g, '/');
    while (s.length % 4) s += '=';
    const json = JSON.parse(atob(s));
    return json.kind === 'attendee' && typeof json.exp === 'number' && json.exp > Date.now();
  } catch {
    return false;
  }
}

export function getAccessToken() {
  if (typeof localStorage === 'undefined') return null;
  const t = localStorage.getItem(TOKEN_KEY);
  return tokenIsValid(t) ? t : null;
}

export default function AccessGate({ children }) {
  const [authed, setAuthed] = useState(() => !!getAccessToken());

  // If the token expires while the tab is open, re-gate on next render.
  useEffect(() => {
    if (!authed) return;
    const id = setInterval(() => {
      if (!getAccessToken()) setAuthed(false);
    }, 60_000);
    return () => clearInterval(id);
  }, [authed]);

  if (authed) return children;
  return <GateScreen onPass={() => setAuthed(true)} />;
}

function GateScreen({ onPass }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const bp = useBreakpoint();
  const isDesktop = bp === 'desktop';

  const submit = async () => {
    if (!code.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/check-access', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ code: code.trim() }),
      });
      if (res.ok) {
        const { token } = await res.json();
        localStorage.setItem(TOKEN_KEY, token);
        onPass();
      } else if (res.status === 401) {
        setError('That code isn’t right. Check your invitation email.');
        setCode('');
      } else {
        setError('Server error — please try again in a moment.');
      }
    } catch {
      setError('Couldn’t reach the server. Check your connection.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: isDesktop ? '#0d1220' : BG,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isDesktop ? '40px' : '32px 24px',
        fontFamily: FONT,
        paddingTop: isDesktop ? '40px' : 'calc(env(safe-area-inset-top, 0px) + 32px)',
      }}
    >
      {isDesktop && (
        <div
          style={{
            background: BG,
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 18,
            padding: '44px 40px 40px',
            width: '100%',
            maxWidth: 420,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            boxShadow: '0 30px 80px rgba(0,0,0,0.5)',
          }}
        >
          <GateBody
            code={code}
            setCode={setCode}
            submit={submit}
            busy={busy}
            error={error}
          />
        </div>
      )}
      {!isDesktop && <GateBody code={code} setCode={setCode} submit={submit} busy={busy} error={error} />}
    </div>
  );
}

function GateBody({ code, setCode, submit, busy, error }) {
  return (
    <>
      <img
        src="/elx-logo-white.png"
        alt="ELX"
        style={{ height: 44, objectFit: 'contain', marginBottom: 10 }}
      />
      <div
        style={{
          fontSize: 11,
          fontWeight: 500,
          color: 'rgba(255,255,255,0.45)',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          marginBottom: 36,
        }}
      >
        Executive Leadership Exchange
      </div>

      <div
        style={{
          fontSize: 16,
          fontWeight: 600,
          color: '#fff',
          marginBottom: 6,
          textAlign: 'center',
        }}
      >
        Welcome
      </div>
      <div
        style={{
          fontSize: 13,
          color: 'rgba(255,255,255,0.55)',
          textAlign: 'center',
          marginBottom: 22,
          lineHeight: 1.5,
          maxWidth: 280,
        }}
      >
        Enter the access code from your invitation email to continue.
      </div>

      <div style={{ width: '100%', maxWidth: 300, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          placeholder="Access code"
          autoFocus
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          style={{
            width: '100%',
            padding: '13px 16px',
            border: error ? `1.5px solid ${ERROR_RED}` : '1.5px solid rgba(255,255,255,0.15)',
            borderRadius: 10,
            background: 'rgba(255,255,255,0.07)',
            color: '#fff',
            fontSize: 16,
            fontFamily: FONT,
            outline: 'none',
            boxSizing: 'border-box',
            letterSpacing: '0.04em',
            textAlign: 'center',
          }}
        />
        {error && (
          <div
            style={{ fontSize: 12.5, color: '#fca5a5', textAlign: 'center', lineHeight: 1.45 }}
          >
            {error}
          </div>
        )}
        <button
          onClick={submit}
          disabled={busy || !code.trim()}
          style={{
            background: busy || !code.trim() ? 'rgba(255,255,255,0.12)' : ACCENT,
            border: 'none',
            borderRadius: 10,
            padding: '13px 20px',
            color: '#fff',
            fontSize: 14,
            fontWeight: 600,
            cursor: busy || !code.trim() ? 'not-allowed' : 'pointer',
            fontFamily: FONT,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          {busy ? 'Checking…' : (<><Ic.Spark size={14} color="#fff" /> Continue</>)}
        </button>
      </div>

      <div
        style={{
          marginTop: 32,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          opacity: 0.35,
        }}
      >
        <span style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.6)' }}>Hosted by</span>
        <img
          src="/chirocongress-logo-white.png"
          alt="ChiroCongress"
          style={{ height: 16, objectFit: 'contain' }}
        />
      </div>
    </>
  );
}
